import { useState } from "react";
import { Box } from "@mui-wrapper";
import { Alert, Dialog, Text, TextField, useToast } from "@flat-ui";
import { AdmPlan, SubjectTypeProps } from "@custom-types/base-types";
import { faroCoreApiClient } from "@api/faro-core-api-client";
import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { SCENE_SEATS } from "@components/plans-table/scene-licenses";
import { getSceneSeatConstraint } from "@utils/plan-utils";
import { createSceneSeatConstraintsPayload } from "@utils/scene-seat-utils";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { fetchPlans } from "@store/plans/plans-thunks";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { PlanEvents } from "@utils/track-event/track-event-list";
import {
  activeMembersLimitSelector,
  sceneSeatsLimitSelector,
} from "@store/plans/plans-selector";

interface Props extends SubjectTypeProps {
  /** The plan the SCENE seats are added for */
  plan: AdmPlan;

  /** Function called when the user closes the dialog */
  onClose(): void;
}

/** Number of seats the dialog proposes to add */
const DEFAULT_SEAT_COUNT = 1;

/**
 * Dialog to add SCENE seats to an existing Sphere XG Point Cloud plan.
 *
 * The seats are added by raising the maximum of the SCENE_SEAT constraint of the plan.
 * The plan may end up with more SCENE seats than active users, which the admin is warned about,
 * but is still allowed to do.
 */
export function AddSceneLicenseDialog({
  plan,
  subjectType,
  onClose,
}: Props): React.JSX.Element {
  const dispatch = useAppDispatch();
  const { trackEvent } = useTrackEvent();
  const { openToast } = useToast();
  const { handleErrorWithDialog } = useErrorHandler();
  const sceneSeatsLimit = useAppSelector(sceneSeatsLimitSelector(plan.id));
  const activeMembersLimit = useAppSelector(
    activeMembersLimitSelector(plan.id)
  );
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  // Kept as text, so that the user can freely edit the number in the input
  const [seatCountText, setSeatCountText] = useState<string>(
    String(DEFAULT_SEAT_COUNT)
  );

  const seatCount = Number(seatCountText);
  const isSeatCountValid = Number.isInteger(seatCount) && seatCount >= 1;

  // The plans created before the SCENE_SEAT constraint count their seats from the attached
  // SCENE seat plans, which the constraint takes over once it is added
  const currentSceneSeats =
    getSceneSeatConstraint(plan)?.data.max ?? sceneSeatsLimit?.totalSeats ?? 0;
  const newSceneSeats = currentSceneSeats + (isSeatCountValid ? seatCount : 0);

  const maxActiveMembers = activeMembersLimit?.max;
  const hasMoreSceneSeatsThanActiveMembers =
    maxActiveMembers !== undefined && newSceneSeats > maxActiveMembers;

  async function handleConfirm(): Promise<void> {
    trackEvent({
      name: PlanEvents.addSceneLicense,
      props: {
        target: subjectType,
        planId: plan.id,
        licenses: SCENE_SEATS.map(({ identifier }) => identifier).join(),
        licenseCount: seatCount,
      },
    });

    try {
      setIsUpdating(true);

      await faroCoreApiClient.admin.updatePlan({
        planId: plan.id,
        updatedPlan: {
          constraints: createSceneSeatConstraintsPayload({
            plan,
            sceneSeats: newSceneSeats,
          }),
        },
      });

      await dispatch(
        fetchPlans({
          subjectType: plan.subject.type,
          subjectId: plan.subject.id,
        })
      ).unwrap();

      openToast({
        title:
          seatCount === 1
            ? "The SCENE seat is added successfully!"
            : "The SCENE seats are added successfully!",
      });
      onClose();
    } catch (error) {
      handleErrorWithDialog({
        id: `add-scene-license-${Date.now().toString()}`,
        title: "Could not add the SCENE seats",
        error,
      });
    } finally {
      setIsUpdating(false);
    }
  }

  return (
    <Dialog
      open={true}
      onCancel={onClose}
      title="Add SCENE seats to this plan"
      onConfirm={handleConfirm}
      showSpinner={isUpdating}
      confirmText="Confirm"
      isConfirmDisabled={!isSeatCountValid}
      confirmTooltip="At least one SCENE seat must be added"
    >
      <Text variant="bodyL">
        Choose how many SCENE seats to add to this plan. They are added to the
        SCENE seats the plan already allows.
      </Text>

      <Box sx={{ mt: 2 }}>
        <TextField
          type="number"
          label="Seats to add"
          text={seatCountText}
          onTextChanged={setSeatCountText}
          controlStyle={{
            "& .MuiOutlinedInput-input": { p: "16.5px 14px" },
          }}
          fullWidth
          disabled={isUpdating}
        />
      </Box>

      {hasMoreSceneSeatsThanActiveMembers && (
        <Alert
          variant="warning"
          title="More SCENE seats than active users"
          sx={{ mt: 2 }}
        >
          <p>
            The plan will allow more SCENE seats than active users. The seats
            can still be added.
          </p>
          <div>Maximum active users: {maxActiveMembers}</div>
          <div>Current SCENE seats: {currentSceneSeats}</div>
          <div>SCENE seats after adding: {newSceneSeats}</div>
        </Alert>
      )}
    </Dialog>
  );
}
